import { Component, Input, OnChanges, SimpleChanges, signal, ViewChild } from '@angular/core';
import { FormArray, FormGroup, UntypedFormControl } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { validateCustomProfile } from 'src/app/perivis/shared/validators/custom-profile.validator';
import { CustomEPP, ExternalPressureProfileType, PressurePoint } from '../../models/external-pressure-profile.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { lastValueFrom } from 'rxjs';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';
import { GetValueFromFeet } from 'src/app/perivis/shared/helpers/units.helper';
import { UnitsGridInputComponent } from 'src/app/shared/components/units-grid-input/units-grid-input.component';
import { UnitsGridColumn, UnitsGridCommitEvent, UnitsGridPasteRange } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import {
  addCustomEppPackerPoints,
  createCustomEppColumns,
  createCustomEppRow,
  ensureTrailingInputRow,
  extractCustomEppRows,
  extractCustomEppPoints,
  findRowIndexNearDepth,
  getCustomEppCellTooltip,
  getCustomEppRowActionIconSrc,
  getCustomEppRowActionLabel,
  isCustomEppProtectedRow,
  populateCustomEppFormArray
} from './custom-epp.helper';

@Component({
    selector: 'app-custom-epp',
    template: `
      <app-tool-loader [isLoading]="isLoading()" [positionUnset]="true">
        <form [formGroup]="form.formGroup">
          <div class="space-x-2 flex items-center pb-2">

            <p-button
              outlined="true"
              size="small"
              icon="pi pi-arrow-up"
              title="Scroll to previous packer"
              (onClick)="scrollToPacker('up')"
            >
            </p-button>
            <p-button
              outlined="true"
              size="small"
              icon="pi pi-arrow-down"
              title="Scroll to next packer"
              (onClick)="scrollToPacker('down')"
            ></p-button>
          </div>

          @if (form.formGroup.controls.pressurePoints.errors?.lastPointAboveShoe) {
            <p class="validation-error">
              Must be specified from hanger: {{ currentString?.hangerMd.toFixed(2) }} {{ userUnits.longLengths }}, to or
              below shoe: {{ currentString?.shoeMd.toFixed(2) }} {{ userUnits.longLengths }}, but above well TD: {{ wellTd.toFixed(2) }} {{ userUnits.longLengths }}
            </p>
          }

          <app-units-grid-input
            #customEppTable
            [tableTitle]="'Custom External Pressures'"
            [rows]="customEppRows"
            [columns]="columns"
            [userUnits]="userUnits"
            [enableScroll]="true"
            [scrollHeight]="'356px'"
            [allowDeleteRows]="true"
            [minRows]="1"
            [createRow]="createRow"
            [rowCanBeDeleted]="canDeleteCustomEppRow"
            [rowActionLabel]="getEppRowActionLabel"
            [rowActionIconSrc]="getEppRowActionIconSrc"
            [cellTooltip]="getEppCellTooltip"
            [commitMode]="'debounced'"
            [commitDebounceMs]="300"
            (cellEdited)="onCellEdited()"
            (rowDeleted)="onRowDeleted($event)"
            (rowsReset)="onRowsReset()"
            (pasteApplying)="onPasteApplying($event)"
            (pasteApplied)="onPasteApplied()"
            (commitRequested)="onGridCommit($event)"
          />
        </form>
      </app-tool-loader>
    `,
    providers: subformComponentProviders(CustomEppComponent),
    standalone: false
})
export class CustomEppComponent implements OnChanges {
  constructor(private _packerService: PackerService) {
    // ngx-sub-form patches `writeValue` (the CVA hook) onto `this` when `form` is created below.
    // For CVA sub-forms, initialization MUST happen only via writeValue to avoid racing against it.
    // Defer to next microtask to let ngx-sub-form's async form updates complete before reading values.
    const cva = this as unknown as { writeValue(value: CustomEPP | null): void };
    const applyWrittenValue = cva.writeValue.bind(cva);
    cva.writeValue = (value: CustomEPP | null): void => {
      applyWrittenValue(value);
      Promise.resolve().then(() => void this.setDataAndDefaults());
    };
  }

  @Input() currentString: any;
  @Input() userUnits: UserUnitsModel;
  @Input() wellTd: number;
  @Input() defaultPressures: PressurePoint[];

  public packerDepths: number[] = [];
  public packerNameDepths: { name: string; depth: number }[] = [];
  public isLoading = signal<boolean>(true);

  @ViewChild("customEppTable")
  public customEppTable: UnitsGridInputComponent;

  public readonly columns = createCustomEppColumns();
  public readonly createRow = createCustomEppRow;
  public readonly getEppRowActionLabel = (_row: FormGroup, rowIndex: number): string | null =>
    getCustomEppRowActionLabel(this.customEppRows.controls, rowIndex, this.packerDepths, this.packerNameDepths);
  public readonly getEppRowActionIconSrc = (_row: FormGroup, rowIndex: number): string | null =>
    getCustomEppRowActionIconSrc(this.customEppRows.controls, rowIndex, this.packerDepths, this.packerNameDepths);
  public readonly getEppCellTooltip = (_row: FormGroup, column: UnitsGridColumn, rowIndex: number): string | null =>
    getCustomEppCellTooltip(this.customEppRows.controls, rowIndex, column.field, this.packerDepths, this.packerNameDepths);
  public readonly canDeleteCustomEppRow = (row: FormGroup, rowIndex: number): boolean =>
    !isCustomEppProtectedRow(this.customEppRows.controls, rowIndex, this.packerDepths, this.packerNameDepths);
  public customEppRows = new FormArray<FormGroup>([]);

  private _scrolledPackerIndex = -1;
  private _isRebuildingPackersAfterPaste = false;

  public form = createForm<CustomEPP>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(ExternalPressureProfileType.CUSTOMEPP),
      pressurePoints: new UntypedFormControl(null)
    }
  });

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.defaultPressures || !this.defaultPressures?.length) {
      return;
    }

    Promise.resolve().then(() => {
      if (!(this.form.formGroup.controls.pressurePoints?.value?.length > 0)) {
        void this.setDataAndDefaults();
      }
    });
  }

  private async setDataAndDefaults(): Promise<void> {
    this.form.formGroup.controls.pressurePoints.addValidators(validateCustomProfile(this.currentString, this.wellTd));
    this.isLoading.set(true);

    let points: PressurePoint[];
    if (this.form.formGroup.controls.pressurePoints?.value?.length > 0) {
      points = this.form.formGroup.controls.pressurePoints.value;
      await this.loadPackerDepths();
    } else if (this.defaultPressures?.length > 0) {
      points = await this.createInitialCustomPressurePoints();
      this.form.formGroup.controls.pressurePoints.setValue(points);
    } else {
      points = await this.createInitialCustomPressurePoints();
      if (this.form.formGroup.controls.pressurePoints?.value?.length > 0 || this.defaultPressures?.length > 0) {
        this.isLoading.set(false);
        return;
      }
      populateCustomEppFormArray(this.customEppRows, points);
      if (this.packerDepths.length > 0) {
        this.disablePackerDepths();
      }
      this.isLoading.set(false);
      return;
    }

    populateCustomEppFormArray(this.customEppRows, points);

    if (this.packerDepths.length > 0) {
      this.disablePackerDepths();
      if (points.length > 10) {
        this.scrollToFirstPacker();
      }
    }

    this.isLoading.set(false);
  }

  private async loadPackerDepths(): Promise<void> {
    const packers = await lastValueFrom(this._packerService.getPackersForTubular());
    this.packerNameDepths = packers.packers.map(p => ({ name: p.name, depth: p.measuredDepth }));
    this.packerDepths = packers.packers.map(p => p.measuredDepth).sort((a, b) => a - b);
  }

  // Runs synchronously on blur so a new trailing row exists before Tab navigation looks for the next cell.
  public onCellEdited(): void {
    ensureTrailingInputRow(this.customEppRows);
  }

  public onRowDeleted(index: number): void {
    const row = this.customEppRows.at(index);
    if (row && this.canDeleteCustomEppRow(row, index) && index >= 0 && index < this.customEppRows.length) {
      this.customEppRows.removeAt(index);
    }
    ensureTrailingInputRow(this.customEppRows);
    this.syncPressurePoints();
  }

  // Must stay synchronous: the grid requests its commit right after emitting rowsReset,
  // so the rebuilt rows need to exist before that commit syncs the form value.
  public onRowsReset(): void {
    const points = this.buildInitialCustomPressurePoints();
    populateCustomEppFormArray(this.customEppRows, points);
    this.form.formGroup.controls.pressurePoints.setValue(points);
    if (this.packerDepths.length > 0) {
      this.disablePackerDepths();
    }
  }

  // A paste covering MD + pressure replaces the profile, so locked packer depths must not block it.
  // The packer rows are rebuilt from the pasted profile in onPasteApplied.
  public onPasteApplying(range: UnitsGridPasteRange): void {
    this._isRebuildingPackersAfterPaste = range.startColumnIndex === 0
      && range.columnCount >= 2
      && this.packerDepths.length > 0;

    if (this._isRebuildingPackersAfterPaste) {
      this.customEppRows.controls.forEach(row => row.get('measuredDepth')?.enable({ emitEvent: false }));
    }
  }

  // Runs before the grid's paste commit, so the commit syncs the rebuilt rows.
  public onPasteApplied(): void {
    if (!this._isRebuildingPackersAfterPaste) {
      return;
    }

    this._isRebuildingPackersAfterPaste = false;
    populateCustomEppFormArray(
      this.customEppRows,
      addCustomEppPackerPoints(
        extractCustomEppPoints(this.customEppRows.controls),
        this.packerDepths,
        this.getDepthAbovePacker()
      )
    );
    this.disablePackerDepths();
    this._scrolledPackerIndex = -1;
  }

  public onGridCommit(_event: UnitsGridCommitEvent): void {
    ensureTrailingInputRow(this.customEppRows);
    this.syncPressurePoints();
    if (this.packerDepths.length > 0) {
      this.disablePackerDepths();
    }
  }

  private syncPressurePoints(): void {
    this.form.formGroup.controls.pressurePoints.setValue(extractCustomEppRows(this.customEppRows));
  }

  private async createInitialCustomPressurePoints(): Promise<PressurePoint[]> {
    await this.loadPackerDepths();
    return this.buildInitialCustomPressurePoints();
  }

  private getDepthAbovePacker(): number {
    return GetValueFromFeet(0.05, this.userUnits.longLengths);
  }

  private buildInitialCustomPressurePoints(): PressurePoint[] {
    const tolerance = 0.01;
    const pressures = (this.defaultPressures ?? [])
      .map(p => ({
        measuredDepth: +p.measuredDepth,
        pressure: +p.pressure.toFixed(3)
      }));

    if (pressures.length > 0) {
      const first = pressures[0];
      const last = pressures[pressures.length - 1];

      const hangerMd = this.currentString?.hangerMd ?? first.measuredDepth;
      if (Math.abs(first.measuredDepth - hangerMd) >= tolerance && hangerMd !== 0) {
        first.measuredDepth = hangerMd - 0.000001;
      }

      if (last.measuredDepth > this.wellTd) {
        last.measuredDepth = this.wellTd - 0.000001;
      }
    } else {
      pressures.push({ measuredDepth: this.currentString?.hangerMd, pressure: 0 });
      pressures.push({ measuredDepth: this.currentString?.shoeMd, pressure: 0 });
    }

    return addCustomEppPackerPoints(pressures, this.packerDepths, this.getDepthAbovePacker(), tolerance);
  }

  private disablePackerDepths(): void {
    this.customEppRows.controls.forEach(row => row.get('measuredDepth')?.enable({ emitEvent: false }));

    this.packerDepths.forEach(packerDepth => {
      const packerRowIndex = findRowIndexNearDepth(this.customEppRows, packerDepth, 0.01);
      if (packerRowIndex === -1) {
        return;
      }

      this.customEppRows.at(packerRowIndex)?.get('measuredDepth')?.disable({ emitEvent: false });
      this.customEppRows.at(packerRowIndex - 1)?.get('measuredDepth')?.disable({ emitEvent: false });
    });
  }

  public scrollToFirstPacker(): void {
    if (this.packerDepths.length === 0) {
      return;
    }

    const rowIndex = findRowIndexNearDepth(this.customEppRows, this.packerDepths[0], 0.01) - 1;
    this._scrolledPackerIndex = rowIndex;
    this.customEppTable?.scrollToRowIndex(rowIndex);
  }

  public scrollToPacker(direction: 'up' | 'down'): void {
    const rows = this.customEppRows.controls;
    const tolerance = 0.01;
    const isPackerRow = (index: number): boolean => {
      const depth = Number(rows[index].get('measuredDepth')?.value);
      return this.packerDepths.some(d => Math.abs(d - depth) < tolerance);
    };

    if (direction === 'down') {
      for (let i = this._scrolledPackerIndex + 1; i < rows.length; i++) {
        if (isPackerRow(i)) {
          this._scrolledPackerIndex = i;
          this.customEppTable?.scrollToRowIndex(i);
          return;
        }
      }
    } else {
      for (let i = this._scrolledPackerIndex - 1; i >= 0; i--) {
        if (isPackerRow(i)) {
          this._scrolledPackerIndex = i;
          this.customEppTable?.scrollToRowIndex(i);
          return;
        }
      }
    }
  }
}
